import { useMemo } from 'react';
import * as yup from 'yup';
import { useUiComponentsInternationalContext } from '../../static-translations/src';

interface YupField {
  label: string;
}

type YupValidationMessage = string | ((field: YupField) => string);

const createLocalizedYup = (
  tt: ReturnType<typeof useUiComponentsInternationalContext>['tt']
) => {
  function requiredLocalized(
    this: any,
    msg?: YupValidationMessage,
    ...rest: any[]
  ) {
    const defaultMsg = (field: YupField) =>
      tt(
        s =>
          s.defaultFormValidationMessagesCommon
            .defaultFieldRequiredValidationMessage,
        {
          label: field.label,
        }
      );

    return this.required(msg ?? defaultMsg, ...rest);
  }

  function maxLocalized(
    this: any,
    length: number,
    msg?: YupValidationMessage,
    ...rest: any[]
  ) {
    const defaultMsg = (field: YupField) =>
      tt(
        s =>
          s.defaultFormValidationMessagesCommon
            .defaultMaxLengthConstraintValidationMessage,
        {
          label: field.label,
          length: length.toString(),
        }
      );

    return this.max(length, msg ?? defaultMsg, ...rest);
  }

  function minLocalized(
    this: any,
    length: number,
    msg?: YupValidationMessage,
    ...rest: any[]
  ) {
    const defaultMsg = (field: YupField) =>
      tt(
        s =>
          s.defaultFormValidationMessagesCommon
            .defaultMinLengthConstraintValidationMessage,
        {
          label: field.label,
          length: length.toString(),
        }
      );

    return this.min(length, msg ?? defaultMsg, ...rest);
  }

  function emailLocalized(
    this: any,
    msg?: YupValidationMessage,
    ...rest: any[]
  ) {
    const defaultMsg = (field: YupField) =>
      tt(
        s =>
          s.defaultFormValidationMessagesCommon.defaultEmailValidationMessage,
        {
          label: field.label,
        }
      );

    return this.email(msg ?? defaultMsg, ...rest);
  }

  function testLocalized(
    this: any,
    name: string,
    msg?: YupValidationMessage,
    test?: () => boolean
  ) {
    const defaultMsg = (field: YupField) =>
      tt(
        s =>
          s.defaultFormValidationMessagesCommon
            .defaultFieldInvalidValidationMessage,
        {
          label: field.label,
        }
      );

    return this.test(name, msg ?? defaultMsg, test);
  }

  yup.addMethod(yup.mixed, 'requiredLocalized', requiredLocalized);
  yup.addMethod(yup.date, 'requiredLocalized', requiredLocalized);
  yup.addMethod(yup.string, 'requiredLocalized', requiredLocalized);
  yup.addMethod(yup.string, 'maxLocalized', maxLocalized);
  yup.addMethod(yup.string, 'minLocalized', minLocalized);
  yup.addMethod(yup.string, 'emailLocalized', emailLocalized);
  yup.addMethod(yup.string, 'testLocalized', testLocalized);

  return yup;
};

const useLocalizedYup = () => {
  const { tt } = useUiComponentsInternationalContext();

  return useMemo(() => createLocalizedYup(tt), [tt]);
};

export default useLocalizedYup;
